import { ComponentType } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { TwcComponentProps } from 'react-twc';

import { Kbd } from '~/modules/elements/akui/kbd';
import Typography from '~/modules/elements/akui/primitives/typography';
import { supportsEscAsBack } from '~/modules/hooks/use-keyboard';
import {
  KeyboardHelpVisibilitySetting,
  MobilePhoneModeSetting,
  useSettingValue,
} from '~/routes/settings/settings-state';
import { twx } from '~/utils/twx';

import { RegularHelpEntry } from './context';

interface Props {
  help: RegularHelpEntry;
}

export default function KeyboardHelpView({ help }: Props) {
  const [mobilePhoneMode] = useSettingValue(MobilePhoneModeSetting);
  const [isVisible, setIsVisible] = useSettingValue(KeyboardHelpVisibilitySetting);

  useHotkeys('shift+h', () => setIsVisible(!isVisible), undefined, [isVisible]);

  // Only keys this view actually has a renderer for. `HelpEntry` also carries remote-mic-only
  // metadata (title, icon, mirrored controls) which the provider strips out — but this view sits at
  // the app root, so an unrecognised key reaching the lookup below would throw and take the whole UI
  // down with it. Skipping unknown keys keeps that failure mode impossible.
  const helps = Object.entries(help ?? {}).filter(([type, value]) => value !== undefined && type in KeyhelpComponent);
  const hasHelp = Object.values(help ?? {}).some((value) => value !== undefined);

  if (mobilePhoneMode) {
    return null;
  }

  return (
    <>
      <Container
        data-test="help-container"
        onClick={() => setIsVisible(!isVisible)}
        data-visible={hasHelp && isVisible}>
        {isVisible && (
          <>
            <UseKeyboardIndicator className="UseKeyboardIndicator">
              Use indicated keys on your keyboard
            </UseKeyboardIndicator>
            {helps.map(([type, label]) => {
              const { view: Component, defaultLabel } = KeyhelpComponent[type as keyof RegularHelpEntry];
              const text = label ?? defaultLabel;
              return (
                <Section key={type} style={{ viewTransitionName: sectionTransitionName(type, text) }}>
                  <SectionKeys>
                    <Component />
                  </SectionKeys>
                  <SectionHelp>{text}</SectionHelp>
                </Section>
              );
            })}
            <Section className="[view-transition-name:help-view-mute]">
              <SectionKeys>{ShiftLetter('m')()}</SectionKeys>
              <SectionHelp>Mute / unmute</SectionHelp>
            </Section>
            <Section className="[view-transition-name:help-view-toggle]">
              <SectionKeys>{ShiftLetter('h')()}</SectionKeys>
              <SectionHelp>Toggle this help</SectionHelp>
            </Section>
          </>
        )}
      </Container>
    </>
  );
}

// A name per key + label, so an entry present (unchanged) on both views stays put during a view
// transition, while one that appears, disappears or changes its label fades in/out as a whole.
const sectionTransitionName = (type: string, label: string) => {
  let hash = 0;
  for (const char of label) hash = (Math.imul(hash, 31) + char.charCodeAt(0)) | 0;
  return `help-view-${type}-${(hash >>> 0).toString(36)}`;
};

const HorizontalVerticalBase = ({ vertical = false, horizontal = false }) => (
  <>
    <Kbd disabled={!vertical}>↑</Kbd>
    <br />
    <Kbd disabled={!horizontal}>←</Kbd>
    <Kbd disabled={!vertical}>↓</Kbd>
    <Kbd disabled={!horizontal}>→</Kbd>
  </>
);

const Horizontal = () => <HorizontalVerticalBase horizontal />;
const Vertical = () => <HorizontalVerticalBase vertical />;
const HorizontalVertical = () => <HorizontalVerticalBase vertical horizontal />;
const Accept = () => <Kbd>Enter ⏎</Kbd>;
const Back = () => (supportsEscAsBack ? <Kbd>Escape</Kbd> : <Kbd>Backspace ⌫</Kbd>);
const ShiftLetter = (letter: string) => () => (
  <>
    <Kbd>Shift</Kbd> + <Kbd>{letter.toUpperCase()}</Kbd>
  </>
);
const Alphanumeric = () => (
  <>
    <Kbd>start typing</Kbd>
  </>
);

const KeyhelpComponent: Record<keyof RegularHelpEntry, { view: ComponentType; defaultLabel: string }> = {
  'horizontal-vertical': { view: HorizontalVertical, defaultLabel: 'Navigate' },
  horizontal: { view: Horizontal, defaultLabel: 'Navigate' },
  vertical: { view: Vertical, defaultLabel: 'Navigate' },
  accept: { view: Accept, defaultLabel: 'Select' },
  back: { view: Back, defaultLabel: 'Go back' },
  shiftR: { view: ShiftLetter('r'), defaultLabel: 'Pick random' },
  alphanumeric: { view: Alphanumeric, defaultLabel: 'Search' },
};

// One entry per column - its keys on top, what they do underneath - so the panel reads as a strip of
// keys rather than a list of sentences, and stays short enough to sit in a corner. `justify-end`
// puts every column's keys against its label, so the labels line up however tall the keys above them
// are (the arrow pad is two rows, every other entry is one).
const Section = twx.div`flex min-w-0 flex-col items-center justify-end gap-1 text-center`;

// `text-sm` here rather than on the `Kbd`s themselves: they size in `em`, so the whole cluster -
// glyphs, padding and border - scales from this one place.
const SectionKeys = twx.div`text-default flex-nowrap text-center text-xs font-bold [&_kbd]:shadow-lg [&_kbd]:shadow-black/70`;

// No panel background behind the entries, so each label carries its own shadow to stay legible.
const SectionHelp = twx(Typography)`text-center text-xs text-balance text-shadow-black text-shadow-lg`;

// Blurs the entries underneath on hover instead of covering them with a dark panel.
const UseKeyboardIndicator = twx(
  Typography,
)`text-md text-default invisible absolute inset-0 flex items-center justify-center rounded-tr-xl opacity-0 backdrop-blur-sm duration-300 text-shadow-black text-shadow-lg hover:visible hover:opacity-100`;

const Container = twx.div((props: TwcComponentProps<'div'> & { 'data-visible': boolean }) => [
  // Bottom left, laid out as a row: a panel of its own in the corner, rather than a column of text
  // down the side of whatever screen is up. The LEFT corner because the right one is where screens
  // put their primary action - "Play next song", the rating buttons - and a panel sitting on top of
  // those swallows the click. Flush into the corner with no background - the entries carry their own
  // shadows. `w-auto` so it is only as wide as its entries, and `items-end` so every column
  // sits on a shared bottom edge whatever the height of the keys above it.
  `z-help fixed bottom-0 left-0 w-auto max-w-screen cursor-pointer flex-row items-end justify-start gap-3 px-1 pr-5 pb-1 pt-5 [view-transition-name:help-view] hover:[&_.UseKeyboardIndicator]:visible hover:[&_.UseKeyboardIndicator]:opacity-100 [&_svg]:fill-white`,
  props['data-visible'] ? 'flex max-lg:hidden' : 'hidden',
]);
